import { useState, useEffect } from 'react';
import { Flame, TrendingUp, Target, DollarSign, Globe } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Card } from '../../components/ui';
import { CURRENT_CARNIVAL_YEAR, SUPPORTED_YEARS } from '../../lib/config';
import type { Band } from '../../types/database';
import styles from './Analytics.module.css';

interface Props {
  band: Band;
}

interface AnalyticsData {
  totalViews: number;
  totalMatches: number;
  totalPasses: number;
  totalConnects: number;
  matchRate: number;
  matchToConnectRate: number;
  avgTimeSpent: number;
  expandedRate: number;
  locationBreakdown: Record<string, number>;
  trinidadAreaBreakdown: Record<string, number>;
  budgetBreakdown: Record<string, number>;
  vibeBreakdown: Record<string, number>;
  experienceBreakdown: Record<string, number>;
  costumeBreakdown: Record<string, number>;
  passReasons: { reason: string; count: number }[];
  // Trends
  interestTrend: { date: string; count: number }[];
  // Headline metrics
  liveInterest24h: number;
  localCount: number;
  diasporaCount: number;
  topSegment: string;
  topCostumeTier: string;
}

const experienceLabels: Record<string, string> = {
  first_time: 'First Timer',
  '1_2_times': '1-2 Times',
  '3_5_times': '3-5 Times',
  veteran: 'Veteran (6+)',
};

const costumeLabels: Record<string, string> = {
  frontline: 'Frontline',
  midline: 'Midline',
  backline: 'Backline',
  any: 'Any Section',
};

const budgetLabels: Record<string, string> = {
  under_500: 'Under $500',
  '500_800': '$500-800',
  '800_1200': '$800-1200',
  '1200_plus': '$1200+',
  unlimited: 'Unlimited',
};

const vibeLabels: Record<string, string> = {
  bacchanal: 'Bacchanal',
  pretty: 'Pretty Mas',
  music: 'Music',
  social: 'Social',
  cultural: 'Cultural',
};

export function Analytics({ band }: Props) {
  const [selectedYear, setSelectedYear] = useState(CURRENT_CARNIVAL_YEAR);
  const [showComparison, setShowComparison] = useState(false);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [previousYearData, setPreviousYearData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalyticsForYear = async (year: number): Promise<AnalyticsData | null> => {
    // Fetch swipes data with user info
    const { data: swipes } = await supabase
      .from('swipes')
      .select('*, user:users(location, trinidad_area, budget_range, vibe_preferences, experience_level, costume_preference)')
      .eq('band_id', band.id)
      .eq('carnival_year', year);

    if (!swipes) return null;

    // Calculate base metrics
    const matches = swipes.filter((s) => s.action === 'match');
    const passes = swipes.filter((s) => s.action === 'pass');
    const connects = swipes.filter((s) => s.action === 'connect');
    const expanded = swipes.filter((s) => s.expanded_profile);
    const withTime = swipes.filter((s) => s.time_spent_seconds);

    // Location breakdown & local vs diaspora
    const locationBreakdown: Record<string, number> = {};
    let localCount = 0;
    let diasporaCount = 0;
    swipes.forEach((s) => {
      const location = (s.user as { location?: string })?.location || 'Unknown';
      locationBreakdown[location] = (locationBreakdown[location] || 0) + 1;
      if (location === 'Trinidad & Tobago') {
        localCount++;
      } else if (location !== 'Unknown') {
        diasporaCount++;
      }
    });

    // Trinidad area breakdown (for local users)
    const trinidadAreaBreakdown: Record<string, number> = {};
    swipes.forEach((s) => {
      const area = (s.user as { trinidad_area?: string })?.trinidad_area;
      if (area) {
        // Extract just the region name (e.g., "North West" from "North West (Port of Spain, Diego Martin)")
        const shortName = area.split(' (')[0];
        trinidadAreaBreakdown[shortName] = (trinidadAreaBreakdown[shortName] || 0) + 1;
      }
    });

    // Budget breakdown (from users who matched)
    const budgetBreakdown: Record<string, number> = {};
    matches.forEach((s) => {
      const budget = (s.user as { budget_range?: string })?.budget_range;
      if (budget) {
        const label = budgetLabels[budget] || budget;
        budgetBreakdown[label] = (budgetBreakdown[label] || 0) + 1;
      }
    });

    // Vibe breakdown (from users who matched)
    const vibeBreakdown: Record<string, number> = {};
    matches.forEach((s) => {
      const vibes = (s.user as { vibe_preferences?: string[] })?.vibe_preferences || [];
      vibes.forEach((v) => {
        const label = vibeLabels[v] || v;
        vibeBreakdown[label] = (vibeBreakdown[label] || 0) + 1;
      });
    });

    // Experience breakdown (from all swipes)
    const experienceBreakdown: Record<string, number> = {};
    swipes.forEach((s) => {
      const exp = (s.user as { experience_level?: string })?.experience_level;
      if (exp) {
        const label = experienceLabels[exp] || exp;
        experienceBreakdown[label] = (experienceBreakdown[label] || 0) + 1;
      }
    });

    // Costume tier breakdown (from matched users)
    const costumeBreakdown: Record<string, number> = {};
    matches.forEach((s) => {
      const costume = (s.user as { costume_preference?: string })?.costume_preference;
      if (costume) {
        const label = costumeLabels[costume] || costume;
        costumeBreakdown[label] = (costumeBreakdown[label] || 0) + 1;
      }
    });

    // Find top segment and costume tier
    const topSegment = Object.entries(experienceBreakdown).sort(([, a], [, b]) => b - a)[0]?.[0] || 'N/A';
    const topCostumeTier = Object.entries(costumeBreakdown).sort(([, a], [, b]) => b - a)[0]?.[0] || 'N/A';

    // Pass reasons (inferred from user preferences vs band attributes)
    const passReasons: { reason: string; count: number }[] = [];
    let budgetMismatch = 0;
    let vibeMismatch = 0;

    passes.forEach((s) => {
      const userBudget = (s.user as { budget_range?: string })?.budget_range;
      if (userBudget === 'under_500' && band.price_min && band.price_min > 500) {
        budgetMismatch++;
      } else if (userBudget === '500_800' && band.price_min && band.price_min > 800) {
        budgetMismatch++;
      }

      const userVibes = (s.user as { vibe_preferences?: string[] })?.vibe_preferences || [];
      const bandVibes = band.vibe_tags || [];
      if (userVibes.length > 0 && !userVibes.some((v) => bandVibes.includes(v as (typeof bandVibes)[number]))) {
        vibeMismatch++;
      }
    });

    if (budgetMismatch > 0) {
      passReasons.push({ reason: 'Budget mismatch', count: budgetMismatch });
    }
    if (vibeMismatch > 0) {
      passReasons.push({ reason: 'Vibe mismatch', count: vibeMismatch });
    }
    passReasons.sort((a, b) => b.count - a.count);

    // Interest trend (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const trendByDate: Record<string, number> = {};
    const interestSwipes = swipes.filter((s) => s.action === 'match' || s.action === 'connect');
    interestSwipes
      .filter((s) => new Date(s.created_at) >= thirtyDaysAgo)
      .forEach((s) => {
        const date = s.created_at.split('T')[0];
        trendByDate[date] = (trendByDate[date] || 0) + 1;
      });
    const interestTrend = Object.entries(trendByDate)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Live interest (last 24 hours)
    const twentyFourHoursAgo = new Date();
    twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);
    const liveInterest24h = interestSwipes.filter((s) => new Date(s.created_at) >= twentyFourHoursAgo).length;

    return {
      totalViews: swipes.length,
      totalMatches: matches.length,
      totalPasses: passes.length,
      totalConnects: connects.length,
      matchRate: swipes.length > 0 ? matches.length / swipes.length : 0,
      matchToConnectRate: matches.length > 0 ? connects.length / matches.length : 0,
      avgTimeSpent:
        withTime.length > 0 ? withTime.reduce((acc, s) => acc + (s.time_spent_seconds || 0), 0) / withTime.length : 0,
      expandedRate: swipes.length > 0 ? expanded.length / swipes.length : 0,
      locationBreakdown,
      trinidadAreaBreakdown,
      budgetBreakdown,
      vibeBreakdown,
      experienceBreakdown,
      costumeBreakdown,
      passReasons,
      interestTrend,
      liveInterest24h,
      localCount,
      diasporaCount,
      topSegment,
      topCostumeTier,
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const currentData = await fetchAnalyticsForYear(selectedYear);
      setData(currentData);

      if (showComparison && selectedYear > SUPPORTED_YEARS[0]) {
        const prevData = await fetchAnalyticsForYear(selectedYear - 1);
        setPreviousYearData(prevData);
      } else {
        setPreviousYearData(null);
      }
      setLoading(false);
    };

    fetchData();
  }, [band.id, band.price_min, band.vibe_tags, selectedYear, showComparison]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (!data) {
    return <div className={styles.empty}>No data available yet</div>;
  }

  const formatPercent = (value: number) => `${Math.round(value * 100)}%`;

  const renderDelta = (current: number, previous: number | undefined, isPercent = false) => {
    if (!showComparison || previous === undefined) return null;
    const delta = current - previous;
    const percentChange = previous > 0 ? Math.round((delta / previous) * 100) : 0;
    const isPositive = delta > 0;
    const displayValue = isPercent ? `${Math.abs(Math.round(delta * 100))}pp` : `${Math.abs(percentChange)}%`;

    return (
      <span className={`${styles.delta} ${isPositive ? styles.positive : styles.negative}`}>
        {isPositive ? '+' : '-'}
        {displayValue}
      </span>
    );
  };

  return (
    <div className={styles.container}>
      {/* Header with year selector */}
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <h1>{band.name} Dashboard</h1>
          <label className={styles.compareToggle}>
            <input type="checkbox" checked={showComparison} onChange={(e) => setShowComparison(e.target.checked)} />
            <span>Compare YoY</span>
          </label>
        </div>

        <div className={styles.yearSelector}>
          {SUPPORTED_YEARS.map((year) => (
            <button
              key={year}
              className={`${styles.yearTab} ${selectedYear === year ? styles.active : ''}`}
              onClick={() => setSelectedYear(year)}
            >
              {year}
              {year === CURRENT_CARNIVAL_YEAR && <span className={styles.currentBadge}>Current</span>}
            </button>
          ))}
        </div>
      </header>

      {/* Headline metrics */}
      <div className={styles.headlineGrid}>
        <Card className={styles.headlineCard}>
          <Flame size={24} className={styles.headlineIcon} />
          <span className={styles.headlineValue}>{data.liveInterest24h}</span>
          <span className={styles.headlineLabel}>Interest (24h)</span>
        </Card>
        <Card className={styles.headlineCard}>
          <TrendingUp size={24} className={styles.headlineIcon} />
          <span className={styles.headlineValue}>
            {formatPercent(data.matchRate)}
            {renderDelta(data.matchRate, previousYearData?.matchRate, true)}
          </span>
          <span className={styles.headlineLabel}>Match Rate</span>
        </Card>
        <Card className={styles.headlineCard}>
          <Target size={24} className={styles.headlineIcon} />
          <span className={styles.headlineValue}>{data.topSegment}</span>
          <span className={styles.headlineLabel}>Top Segment</span>
        </Card>
        <Card className={styles.headlineCard}>
          <DollarSign size={24} className={styles.headlineIcon} />
          <span className={styles.headlineValue}>{data.topCostumeTier}</span>
          <span className={styles.headlineLabel}>Top Costume Tier</span>
        </Card>
        <Card className={styles.headlineCard}>
          <Globe size={24} className={styles.headlineIcon} />
          <span className={styles.headlineValue}>
            {data.localCount > data.diasporaCount ? 'Local' : 'Diaspora'}
          </span>
          <span className={styles.headlineLabel}>
            {Math.round((Math.max(data.localCount, data.diasporaCount) / (data.localCount + data.diasporaCount || 1)) * 100)}% of audience
          </span>
        </Card>
      </div>

      {/* Conversion Funnel */}
      <Card className={styles.funnelCard}>
        <h3>Conversion Funnel</h3>
        <div className={styles.funnel}>
          <div className={styles.funnelStep}>
            <span className={styles.funnelValue}>
              {data.totalViews}
              {renderDelta(data.totalViews, previousYearData?.totalViews)}
            </span>
            <span className={styles.funnelLabel}>Views</span>
          </div>
          <div className={styles.funnelArrow}>→</div>
          <div className={styles.funnelStep}>
            <span className={styles.funnelValue}>
              {data.totalMatches}
              {renderDelta(data.totalMatches, previousYearData?.totalMatches)}
            </span>
            <span className={styles.funnelLabel}>Matches</span>
            <span className={styles.funnelRate}>{formatPercent(data.matchRate)}</span>
          </div>
          <div className={styles.funnelArrow}>→</div>
          <div className={styles.funnelStep}>
            <span className={styles.funnelValue}>
              {data.totalConnects}
              {renderDelta(data.totalConnects, previousYearData?.totalConnects)}
            </span>
            <span className={styles.funnelLabel}>Committed</span>
            <span className={styles.funnelRate}>{formatPercent(data.matchToConnectRate)}</span>
          </div>
        </div>
      </Card>

      {/* Interest Trend */}
      {data.interestTrend.length > 0 && (() => {
        const maxCount = Math.max(...data.interestTrend.map((p) => p.count));
        const midCount = Math.round(maxCount / 2);
        const formatDate = (dateStr: string) => {
          const date = new Date(dateStr);
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        };
        return (
          <Card className={styles.trendCard}>
            <h3>Interest Trend (Last 30 Days)</h3>
            <p className={styles.trendSubtitle}>Daily matches + commits</p>
            <div className={styles.trendChartWrapper}>
              <div className={styles.trendYAxis}>
                <span>{maxCount}</span>
                <span>{midCount}</span>
                <span>0</span>
              </div>
              <div className={styles.trendChartArea}>
                <div className={styles.trendChart}>
                  {data.interestTrend.map((point) => {
                    const height = maxCount > 0 ? (point.count / maxCount) * 100 : 0;
                    return (
                      <div key={point.date} className={styles.trendBar}>
                        <div className={styles.trendBarFill} style={{ height: `${height}%` }} />
                        <div className={styles.trendTooltip}>
                          <span className={styles.trendTooltipDate}>{formatDate(point.date)}</span>
                          <span className={styles.trendTooltipCount}>{point.count} interest{point.count !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className={styles.trendLabels}>
                  <span>{data.interestTrend[0]?.date.slice(5)}</span>
                  <span>{data.interestTrend[data.interestTrend.length - 1]?.date.slice(5)}</span>
                </div>
              </div>
            </div>
          </Card>
        );
      })()}

      {/* Overview stats */}
      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <span className={styles.statValue}>
            {data.totalViews}
            {renderDelta(data.totalViews, previousYearData?.totalViews)}
          </span>
          <span className={styles.statLabel}>Total Views</span>
        </Card>
        <Card className={styles.statCard}>
          <span className={styles.statValue}>
            {data.totalMatches}
            {renderDelta(data.totalMatches, previousYearData?.totalMatches)}
          </span>
          <span className={styles.statLabel}>Matches</span>
        </Card>
        <Card className={styles.statCard}>
          <span className={styles.statValue}>
            {formatPercent(data.matchRate)}
            {renderDelta(data.matchRate, previousYearData?.matchRate, true)}
          </span>
          <span className={styles.statLabel}>Match Rate</span>
        </Card>
        <Card className={styles.statCard}>
          <span className={styles.statValue}>
            {data.totalPasses}
            {renderDelta(data.totalPasses, previousYearData?.totalPasses)}
          </span>
          <span className={styles.statLabel}>Passes</span>
        </Card>
      </div>

      {/* Engagement stats */}
      <div className={styles.row}>
        <Card className={styles.card}>
          <h3>Engagement</h3>
          <div className={styles.engagement}>
            <div className={styles.engagementItem}>
              <span className={styles.engagementValue}>{Math.round(data.avgTimeSpent)}s</span>
              <span className={styles.engagementLabel}>Avg. time on card</span>
            </div>
            <div className={styles.engagementItem}>
              <span className={styles.engagementValue}>{formatPercent(data.expandedRate)}</span>
              <span className={styles.engagementLabel}>Expanded profile</span>
            </div>
            <div className={styles.engagementItem}>
              <span className={styles.engagementValue}>{data.totalConnects}</span>
              <span className={styles.engagementLabel}>Committed</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Audience breakdown */}
      <div className={styles.row}>
        <Card className={styles.card}>
          <h3>Audience Location</h3>
          {Object.entries(data.locationBreakdown).length > 0 ? (
            <div className={styles.breakdown}>
              {Object.entries(data.locationBreakdown)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5)
                .map(([location, count]) => (
                  <div key={location} className={styles.breakdownItem}>
                    <span>{location}</span>
                    <div className={styles.breakdownBar}>
                      <div style={{ width: `${(count / data.totalViews) * 100}%` }} />
                    </div>
                    <span className={styles.breakdownCount}>{count}</span>
                  </div>
                ))}
            </div>
          ) : (
            <p className={styles.noData}>No location data yet</p>
          )}
        </Card>

        <Card className={styles.card}>
          <h3>Trinidad Pickup Area</h3>
          {Object.entries(data.trinidadAreaBreakdown).length > 0 ? (
            <div className={styles.breakdown}>
              {Object.entries(data.trinidadAreaBreakdown)
                .sort(([, a], [, b]) => b - a)
                .map(([area, count]) => (
                  <div key={area} className={styles.breakdownItem}>
                    <span>{area}</span>
                    <div className={styles.breakdownBar}>
                      <div style={{ width: `${(count / data.totalViews) * 100}%` }} />
                    </div>
                    <span className={styles.breakdownCount}>{count}</span>
                  </div>
                ))}
            </div>
          ) : (
            <p className={styles.noData}>No Trinidad area data yet</p>
          )}
        </Card>
      </div>

      {/* Experience breakdown */}
      <div className={styles.row}>
        <Card className={styles.card}>
          <h3>Experience Level</h3>
          {Object.entries(data.experienceBreakdown).length > 0 ? (
            <div className={styles.breakdown}>
              {Object.entries(data.experienceBreakdown)
                .sort(([, a], [, b]) => b - a)
                .map(([exp, count]) => (
                  <div key={exp} className={styles.breakdownItem}>
                    <span>{exp}</span>
                    <div className={styles.breakdownBar}>
                      <div style={{ width: `${(count / data.totalViews) * 100}%` }} />
                    </div>
                    <span className={styles.breakdownCount}>{count}</span>
                  </div>
                ))}
            </div>
          ) : (
            <p className={styles.noData}>No experience data yet</p>
          )}
        </Card>
      </div>

      {/* Budget & Costume */}
      <div className={styles.row}>
        <Card className={styles.card}>
          <h3>Budget Range (Matched Users)</h3>
          {Object.entries(data.budgetBreakdown).length > 0 ? (
            <div className={styles.breakdown}>
              {Object.entries(data.budgetBreakdown)
                .sort(([, a], [, b]) => b - a)
                .map(([budget, count]) => (
                  <div key={budget} className={styles.breakdownItem}>
                    <span>{budget}</span>
                    <div className={styles.breakdownBar}>
                      <div style={{ width: `${(count / data.totalMatches) * 100}%` }} />
                    </div>
                    <span className={styles.breakdownCount}>{count}</span>
                  </div>
                ))}
            </div>
          ) : (
            <p className={styles.noData}>No budget data yet</p>
          )}
        </Card>

        <Card className={styles.card}>
          <h3>Costume Tier Interest</h3>
          {Object.entries(data.costumeBreakdown).length > 0 ? (
            <div className={styles.breakdown}>
              {Object.entries(data.costumeBreakdown)
                .sort(([, a], [, b]) => b - a)
                .map(([costume, count]) => (
                  <div key={costume} className={styles.breakdownItem}>
                    <span>{costume}</span>
                    <div className={styles.breakdownBar}>
                      <div style={{ width: `${(count / data.totalMatches) * 100}%` }} />
                    </div>
                    <span className={styles.breakdownCount}>{count}</span>
                  </div>
                ))}
            </div>
          ) : (
            <p className={styles.noData}>No costume data yet</p>
          )}
        </Card>
      </div>

      {/* Vibe preferences & Pass reasons */}
      <div className={styles.row}>
        <Card className={styles.card}>
          <h3>Vibe Preferences (Matched Users)</h3>
          {Object.entries(data.vibeBreakdown).length > 0 ? (
            <div className={styles.breakdown}>
              {Object.entries(data.vibeBreakdown)
                .sort(([, a], [, b]) => b - a)
                .map(([vibe, count]) => (
                  <div key={vibe} className={styles.breakdownItem}>
                    <span>{vibe}</span>
                    <div className={styles.breakdownBar}>
                      <div style={{ width: `${(count / data.totalMatches) * 100}%` }} />
                    </div>
                    <span className={styles.breakdownCount}>{count}</span>
                  </div>
                ))}
            </div>
          ) : (
            <p className={styles.noData}>No vibe data yet</p>
          )}
        </Card>

        <Card className={styles.card}>
          <h3>Why Users Passed</h3>
          {data.passReasons.length > 0 ? (
            <div className={styles.breakdown}>
              {data.passReasons.map(({ reason, count }) => (
                <div key={reason} className={styles.breakdownItem}>
                  <span>{reason}</span>
                  <div className={styles.breakdownBar}>
                    <div style={{ width: `${(count / data.totalPasses) * 100}%` }} />
                  </div>
                  <span className={styles.breakdownCount}>{Math.round((count / data.totalPasses) * 100)}%</span>
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.noData}>Not enough data to analyze passes</p>
          )}
        </Card>
      </div>
    </div>
  );
}
